import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ApAgentAction,
  ApQuickbooksBillPushStatus,
} from "@/accounts-payable/types";
import { useApi } from "@/core/api";
import {
  invoiceAgentRunsKey,
  invoiceQuickbooksBillKey,
  invoicesKey,
  reviewQueueKey,
} from "./keys";
import { AP_STALE_TIME } from "./polling";

export const useGetQuickbooksBillPushStatus = (
  businessId?: string | null,
  invoiceId?: string | null,
  { enabled = true } = {},
) => {
  const api = useApi();
  return useQuery({
    queryKey: invoiceQuickbooksBillKey(businessId, invoiceId),
    queryFn: () =>
      api
        .get<ApQuickbooksBillPushStatus>(
          `/businesses/${businessId}/ap/invoices/${invoiceId}/quickbooks_bill`,
        )
        .then((response) => response.data),
    enabled: !!businessId && !!invoiceId && enabled,
    staleTime: AP_STALE_TIME,
  });
};

export const usePushInvoiceToQuickbooks = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: string) =>
      api
        .post<ApAgentAction>(
          `/businesses/${businessId}/ap/invoices/${invoiceId}/quickbooks_bill`,
        )
        .then((response) => response.data),
    onSettled: (_action, _error, invoiceId) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: invoicesKey(businessId) }),
        queryClient.invalidateQueries({
          queryKey: invoiceAgentRunsKey(businessId, invoiceId),
        }),
        queryClient.invalidateQueries({
          queryKey: reviewQueueKey(businessId),
        }),
      ]),
  });
};
